import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import AdminViewHeader from "../components/AdminViewHeader.jsx";
import { allowsView, permissionForView } from "../components/AdminMenu.jsx";
import DataTable from "../components/DataTable.jsx";
import OverlayDialog from "../components/OverlayDialog.jsx";
import { EndpointAssignmentEditor } from "../components/endpoints/EndpointAssignmentsSection.jsx";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";
import EmptyState from "../components/EmptyState.jsx";
import { COLUMN_SORT_FIELD, renderInventoryCell } from "../components/endpoints/inventoryTable.jsx";
import { useDataTableColumns } from "../lib/columnSelection.js";
import { ENTITLEMENTS, hasEntitlement } from "../lib/entitlements.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "../lib/authTokenStore.js";
import { useMyPermissions } from "../lib/permissions.js";
import { restGet } from "../lib/restClient.js";
import { useDebouncedValue } from "../lib/useDebouncedValue.js";
import { NOTE_CLASS } from "../lib/ui.js";

const COLUMNS = [
    { id: "hostname", label: "Hostname", width: 240 },
    { id: "platform", label: "Operating system", width: 150 },
    { id: "agentVersion", label: "Agent version", width: 130 },
    { id: "snapshotTime", label: "Last reported", width: 170 },
    { id: "assignment", label: "Assignment", width: 150, sortable: false },
];
const BUTTON = "rounded-[2px] border border-[color:var(--line)] px-[10px] py-[5px] text-[11px] text-[color:var(--ink)] disabled:opacity-50 cursor-pointer";

export default function UnassignedEndpointsPage({ entitlements = null }) {
    const caps = useMyPermissions();
    const identity = useSyncExternalStore(onAuthIdentityChange, getAuthIdentityEpoch, getAuthIdentityEpoch);
    if (!hasEntitlement(entitlements, ENTITLEMENTS.ABAC)
        || !allowsView(permissionForView("unassigned-endpoints"), caps)) return null;
    return <UnassignedInventory key={identity} />;
}

function UnassignedInventory() {
    const [search, setSearch] = useState("");
    const [debouncedSearch] = useDebouncedValue(search.trim(), 250);
    const [refresh, setRefresh] = useState(0);
    const [selected, setSelected] = useState(null);
    const [sortBy, setSortBy] = useState({ id: "snapshotTime", dir: "desc" });
    const onSortChange = useCallback(id => setSortBy(current => ({
        id, dir: current.id === id && current.dir === "asc" ? "desc" : "asc",
    })), []);
    const refreshButton = useRef(null);
    const changed = useCallback(() => setRefresh(value => value + 1), []);
    return <>
        <AdminViewHeader title="Unassigned endpoints" actions={
            <button ref={refreshButton} type="button" className={BUTTON} onClick={changed}>Refresh</button>
        } />
        <div className="grid gap-[8px] py-[12px]">
            <label className="grid gap-[5px]">
                <span className={NOTE_CLASS}>Search hostname</span>
                <input type="search" value={search} maxLength={200} onChange={event => setSearch(event.target.value)}
                    className="min-h-[32px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] text-[color:var(--ink)]"
                    placeholder="Search unassigned endpoints" />
            </label>
        </div>
        <InventoryPage key={`${debouncedSearch}:${refresh}:${sortBy.id}:${sortBy.dir}`} search={debouncedSearch}
            sortBy={sortBy} onSortChange={onSortChange} onSelect={setSelected} />
        {selected && <AssignmentDialog endpoint={selected} onClose={() => setSelected(null)}
            onChanged={changed} fallbackFocus={refreshButton} />}
    </>;
}

function AssignmentDialog({ endpoint, onClose, onChanged, fallbackFocus }) {
    const content = useRef(null);
    useEffect(() => {
        const previous = document.activeElement;
        const dialog = content.current?.closest('[role="dialog"]');
        const focusable = () => [...(dialog?.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]') ?? [])];
        focusable()[0]?.focus();
        const trapFocus = event => {
            if (event.key !== "Tab") return;
            const items = focusable();
            const first = items[0], last = items.at(-1);
            if (event.shiftKey && (document.activeElement === first || !dialog?.contains(document.activeElement))) {
                event.preventDefault(); last?.focus();
            } else if (!event.shiftKey && (document.activeElement === last || !dialog?.contains(document.activeElement))) {
                event.preventDefault(); first?.focus();
            }
        };
        dialog?.addEventListener("keydown", trapFocus);
        return () => {
            dialog?.removeEventListener("keydown", trapFocus);
            (previous?.isConnected ? previous : fallbackFocus.current)?.focus();
        };
    }, [fallbackFocus]);
    return <OverlayDialog open onClose={onClose} title={endpoint.hostname || "Endpoint assignment"}
        eyebrow="Directory-user assignment" analyticsId={DIALOG.ENDPOINT_ASSIGNMENT}>
        <div ref={content}><EndpointAssignmentEditor endpointId={endpoint.id}
            endpointLabel={endpoint.hostname} onChanged={onChanged} /></div>
    </OverlayDialog>;
}

function InventoryPage({ search, sortBy, onSortChange, onSelect }) {
    const [cursor, setCursor] = useState({ id: "", value: "" });
    const [previous, setPrevious] = useState([]);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [retry, setRetry] = useState(0);
    const cols = useDataTableColumns({ storageKey: "unassignedEndpoints", columns: COLUMNS, defaultVisible: COLUMNS.map(column => column.id) });
    useEffect(() => {
        let current = true;
        setLoading(true);
        setError("");
        setResult(null);
        restGet("/v1/endpoints/unassigned", {
            page_size: 50, after_endpoint_id: cursor.id, after_sort_value: cursor.value, search,
            sort_field: COLUMN_SORT_FIELD[sortBy.id], sort_desc: sortBy.dir === "desc",
        })
            .then(value => { if (current) setResult(value); })
            .catch(() => { if (current) setError("Could not load unassigned endpoints. Retry or refresh the page."); })
            .finally(() => { if (current) setLoading(false); });
        return () => { current = false; };
    }, [cursor, search, retry, sortBy]);
    const rows = (result?.endpoints ?? []).map(endpoint => ({
        id: endpoint.endpointId ?? endpoint.endpoint_id,
        hostname: endpoint.hostname,
        platform: endpoint.operatingSystem ?? endpoint.operating_system,
        agentVersion: endpoint.agentVersion ?? endpoint.agent_version,
        snapshotTime: endpoint.lastSeen || endpoint.last_seen ? new Date(endpoint.lastSeen ?? endpoint.last_seen) : null,
    }));
    const next = result?.nextAfterEndpointId ?? result?.next_after_endpoint_id ?? "";
    const nextValue = result?.nextAfterSortValue ?? result?.next_after_sort_value ?? "";
    return <div className="flex min-h-0 flex-1 flex-col" aria-busy={loading}>
        {error ? <div role="alert" className="flex items-center gap-[10px] py-[8px] text-[12px]">
            <span>{error}</span><button type="button" className={BUTTON} onClick={() => setRetry(value => value + 1)}>Retry</button>
        </div> : null}
        <DataTable cols={cols} rows={rows} sortBy={sortBy} onSortChange={onSortChange} getRowId={row => row.id} onRowClick={onSelect}
            renderCell={(row, column) => column.id === "assignment"
                ? <span className="text-[11px] underline">Manage assignment</span> : renderInventoryCell(row, column)}
            loading={loading} skeletonRows={6}
            empty={error ? null : <EmptyState title={next ? "No assignable endpoints on this page" : search ? "No matching unassigned endpoints" : "No unassigned endpoints"}
                note={next ? "Select Next to continue." : search ? "Try a different hostname." : "There are no endpoints awaiting a directory-user assignment."} />}
            footer={<div className="flex items-center justify-between py-[10px]">
                <span className={NOTE_CLASS}>{rows.length} shown</span>
                <div className="flex gap-[8px]">
                    <button type="button" className={BUTTON} disabled={loading || !previous.length} onClick={() => {
                        setCursor(previous.at(-1)); setPrevious(values => values.slice(0, -1));
                    }}>Previous</button>
                    <button type="button" className={BUTTON} disabled={loading || !next} onClick={() => {
                        setPrevious(values => [...values, cursor]); setCursor({ id: next, value: nextValue });
                    }}>Next</button>
                </div>
            </div>} />
    </div>;
}
